// Full Leaderboard page — daily/weekly game-wins ranking.
// Design source: claude.ai design project "Circlechell leaderboard dashboard"
// (CircleChess Leaderboard.dc.html + react-native reference), with the
// design's "Points" column replaced by Rating per product decision.
//
// Layout: the breadcrumb, title, tabs and (wide) your-rank rail stay fixed;
// only the ranked rows scroll inside the list card. The screen fills its
// navigator container height (100% on web) so the inner ScrollView is bounded.
// (The screen is registered in isChessAnalysisScreen so the navigator wraps it
// in a View, not a ScrollView — otherwise the whole page would scroll.)

import React, {useState} from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import CCText from '@/Atoms/CCText';
import CCLoader from '@/Atoms/CCLoader';
import {ScreenName} from '@/navigators/StackConstants';
import {logEvent} from '@/Utils/GAEvents';
import {WIDE_BREAKPOINT, lbColors, lbRadius, lbShadow} from './leaderboardTheme';
import {Period, periodMeta} from './types';
import {useLeaderboardData} from './useLeaderboardData';
import LeaderboardRow from './components/LeaderboardRow';
import LiveBadge from './components/LiveBadge';
import PeriodTabs from './components/PeriodTabs';
import StickyYourRankBar from './components/StickyYourRankBar';
import YourRankPanel from './components/YourRankPanel';

const TOP_N = 20;

function Leaderboard() {
  const navigation = useNavigation();
  const {width} = useWindowDimensions();
  const wide = width >= WIDE_BREAKPOINT;
  const [period, setPeriod] = useState<Period>('daily');
  const meta = periodMeta(period);
  const {rows, me, totalPlayers, leaderWins, isLoading, isError} =
    useLeaderboardData(period, {refetchIntervalMs: 25000});

  const top = rows.slice(0, TOP_N);
  const meInTop = me?.rank != null && me.rank <= TOP_N;
  const showStickyBar = !wide && me != null && !meInTop;

  const onChangePeriod = (p: Period) => {
    setPeriod(p);
    logEvent('event', 'leaderboard_period_change', p);
  };

  if (isLoading) {
    return <CCLoader loading bgWhite />;
  }

  return (
    <View
      style={[
        styles.screen,
        Platform.OS === 'web' ? ({height: '100%'} as any) : null,
      ]}>
      <View style={styles.pageBody}>
        {/* breadcrumb (static) */}
        <Pressable
          onPress={() => navigation.navigate(ScreenName.Dashboard)}
          style={styles.back}>
          <CCText fontSize={13.5} fontWeight="bold" color={lbColors.muted}>
            ← Dashboard
          </CCText>
          <CCText fontSize={13.5} fontWeight="bold" color={lbColors.ink}>
            {' / Leaderboard'}
          </CCText>
        </Pressable>

        {/* title (static) */}
        <View style={styles.titleRow}>
          <View style={styles.titleIcon}>
            <CCText fontSize={30}>🏆</CCText>
          </View>
          <View style={{flex: 1}}>
            <View style={styles.titleInline}>
              <CCText fontSize={32} fontWeight="bold" color={lbColors.ink}>
                Leaderboard
              </CCText>
              <LiveBadge />
            </View>
            <CCText
              fontSize={14}
              fontWeight="semibold"
              color={lbColors.muted}
              style={{marginTop: 2}}>
              Ranked by wins {meta.winWord} · reshuffles as games finish
            </CCText>
          </View>
        </View>

        {/* period tabs (static) */}
        <View style={{marginBottom: 22}}>
          <PeriodTabs period={period} onChange={onChangePeriod} />
        </View>

        {isError ? (
          <View style={[styles.listCard, lbShadow.card, styles.emptyCard]}>
            <CCText fontSize={15} fontWeight="semibold" color={lbColors.muted}>
              Couldn't load the leaderboard. Please try again in a moment.
            </CCText>
          </View>
        ) : (
          <View style={[styles.columns, wide && styles.columnsWide]}>
            {/* ranked list — fills remaining height; only the rows scroll */}
            <View style={styles.listColumn}>
              <View style={[styles.listCard, styles.listCardFill, lbShadow.card]}>
                {/* column header (static) */}
                <View style={styles.colHead}>
                  <CCText
                    fontSize={11}
                    fontWeight="bold"
                    color={lbColors.faint}
                    style={[styles.colHeadText, styles.colRank]}>
                    #
                  </CCText>
                  <View style={{width: 40}} />
                  <CCText
                    fontSize={11}
                    fontWeight="bold"
                    color={lbColors.faint}
                    style={[styles.colHeadText, {flex: 1}]}>
                    PLAYER
                  </CCText>
                  <CCText
                    fontSize={11}
                    fontWeight="bold"
                    color={lbColors.faint}
                    style={[styles.colHeadText, styles.colTrend]}>
                    TREND
                  </CCText>
                  {wide ? (
                    <CCText
                      fontSize={11}
                      fontWeight="bold"
                      color={lbColors.faint}
                      style={[styles.colHeadText, styles.colRating]}>
                      RATING
                    </CCText>
                  ) : null}
                  <CCText
                    fontSize={11}
                    fontWeight="bold"
                    color={lbColors.faint}
                    style={[styles.colHeadText, styles.colWins]}>
                    WINS
                  </CCText>
                </View>

                {top.length === 0 ? (
                  <View style={styles.emptyCard}>
                    <CCText
                      fontSize={15}
                      fontWeight="semibold"
                      color={lbColors.muted}>
                      No wins {meta.winWord} yet — win a game to top the board!
                    </CCText>
                  </View>
                ) : (
                  <ScrollView
                    style={styles.rowsScroll}
                    contentContainerStyle={{
                      paddingBottom: showStickyBar ? 100 : 6,
                    }}>
                    {top.map(entry => (
                      <LeaderboardRow
                        key={entry.userid}
                        entry={entry}
                        wide={wide}
                      />
                    ))}
                  </ScrollView>
                )}
              </View>
            </View>

            {/* your-rank rail (wide only, static) */}
            {wide && me ? (
              <View style={styles.railWrap}>
                <YourRankPanel
                  me={me}
                  meta={meta}
                  totalPlayers={totalPlayers}
                  leaderWins={leaderWins}
                />
              </View>
            ) : null}
          </View>
        )}
      </View>

      {/* sticky your-rank bar — narrow screens, outside the top 20 */}
      {showStickyBar && me ? <StickyYourRankBar me={me} topN={TOP_N} /> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {flex: 1, backgroundColor: lbColors.bg},
  pageBody: {
    flex: 1,
    minHeight: 0,
    maxWidth: 1180,
    alignSelf: 'center',
    width: '100%',
    padding: 22,
  },
  back: {flexDirection: 'row', alignItems: 'center', marginBottom: 16},
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    marginBottom: 20,
  },
  titleIcon: {
    width: 56,
    height: 56,
    borderRadius: 16,
    backgroundColor: lbColors.orange,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleInline: {flexDirection: 'row', alignItems: 'center', gap: 12},
  // content area fills the space under the static header
  columns: {gap: 22, flex: 1, minHeight: 0},
  columnsWide: {flexDirection: 'row', alignItems: 'stretch'},
  listColumn: {flex: 1, minHeight: 0},
  listCard: {
    backgroundColor: lbColors.card,
    borderRadius: lbRadius.xl,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  // fill the column height so the inner ScrollView is bounded and scrolls
  listCardFill: {flex: 1, minHeight: 0},
  rowsScroll: {flex: 1, minHeight: 0},
  railWrap: {alignSelf: 'flex-start'},
  colHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 16,
    paddingBottom: 10,
    paddingTop: 4,
    borderBottomWidth: 1,
    borderBottomColor: lbColors.line,
  },
  colHeadText: {letterSpacing: 0.5},
  colRank: {width: 34, textAlign: 'center'},
  colTrend: {width: 46, textAlign: 'center'},
  colRating: {width: 84, textAlign: 'right'},
  colWins: {width: 60, textAlign: 'right'},
  emptyCard: {padding: 28, alignItems: 'center'},
});

export default Leaderboard;
